/**
 * Component: Toggle
 * ------------------------------------------------------------------------------
 * A accessible function to trigger a toggle-able component with a window overlay.
 *
 * @namespace Toggle
 *
 */
import * as a11y from '@shopify/theme-a11y';

import cssClasses from '../helpers/cssClasses';
import {extendDefaults, on} from '../helpers/utils';
import bind from '../mixins/bind';
import escEvent from '../mixins/esc-event';
import overlay from '../mixins/overlay';

/**
 * Export default toggle module.
 */
export default (config) => {

  /**
   * Instance default settings.
   */
  const defaults = {
    overlay: true,
    focusInput: true,
  };

  /**
   * Instance settings.
   */
  const settings = extendDefaults(defaults, config);
  const namespace = config.namespace || config.targetSelector;

  /**
   * Node selectors.
   */
  const nodeSelectors = {
    toggleSelector: document.querySelectorAll(`[js-toggle="${config.toggleSelector}"]`),
    targetSelector: document.querySelectorAll(`[js-toggle="${config.targetSelector}"]`),
  };

  /**
   * Initialise component bind to prevent double binds.
   */
  const binder = bind(nodeSelectors.targetSelector, {
    className: 'toggle-set',
  });

  /**
   * Initiate component.
   */
  function init() {
    if (binder.isSet()) {
      return;
    }

    setToggleEvents();
    setListeners();
    binder.set();
  }

  /**
   * Set component listeners.
   */
  function setListeners() {
    Frame.EventBus.listen([
      'EscEvent:on',
      `Overlay:${namespace}:hide`,
    ], () => {
      close();
    });
  }

  /**
   * Set click events on toggle selectors.
   */
  function setToggleEvents() {
    [...nodeSelectors.toggleSelector].forEach((element) => {
      on('click', element, (event) => handleToggleEvent(event));
    });
  }

  /**
   * Handle toggle events.
   * @param {Event} event Click event.
   */
  function handleToggleEvent(event) {
    event.preventDefault();
    toggle();
  }

  /**
   * Toggle target component.
   */
  function toggle() {
    return isTargetActive() ? close() : open();
  }

  /**
   * Open target component and fire global open event.
   */
  function open() {
    addSelectorClasses();
    escEvent().set();
    focusTarget();

    if (settings.overlay) {
      overlay({namespace}).open();
    }

    Frame.EventBus.emit(`Toggle:${namespace}:open`);
  }

  /**
   * Close target component and fire global close event.
   */
  function close() {
    removeSelectorClasses();
    unfocusTarget();

    if (settings.overlay) {
      overlay({namespace}).close();
    }

    Frame.EventBus.emit(`Toggle:${namespace}:close`);
  }

  /**
   * Set toggle and target states active.
   */
  function addSelectorClasses() {
    [...nodeSelectors.targetSelector].forEach((element) => {
      element.classList.add(cssClasses.active);
    });

    [...nodeSelectors.toggleSelector].forEach((element) => {
      element.classList.add(cssClasses.active);
    });
  }

  /**
   * Remove active classes from all toggles and targets.
   */
  function removeSelectorClasses() {
    [...nodeSelectors.targetSelector].forEach((element) => {
      element.classList.remove(cssClasses.active);
    });

    [...nodeSelectors.toggleSelector].forEach((element) => {
      element.classList.remove(cssClasses.active);
    });
  }

  /**
   * Check if target is active.
   */
  function isTargetActive() {
    return [...nodeSelectors.targetSelector].every((element) => {
      return element.classList.contains(cssClasses.active);
    });
  }

  /**
   * Set a11y focus on first target.
   */
  function focusTarget() {
    if (settings.focusInput && targetHasInput(nodeSelectors.targetSelector[0])) {
      a11y.trapFocus(nodeSelectors.targetSelector[0].querySelector('input'));
      return;
    }

    a11y.trapFocus(nodeSelectors.targetSelector[0]);
  }

  /**
   * Check if focus has an input to focus on.
   */
  function targetHasInput(target) {
    return target.querySelector('input');
  }

  /**
   * Remove a11y focus on target.
   */
  function unfocusTarget() {
    a11y.removeTrapFocus(nodeSelectors.targetSelector[0]);
  }

  /**
   * Return immutable object.
   */
  return Object.freeze({
    init,
    open,
    close,
  });
};
